import React, { useState } from 'react';
import ButtonGroup from '@eika-infrastruktur/ui-button-group';
import classnames from 'classnames';

import { Alternative, Question, useConfig } from './default-config';
import Start from './Start';
import Results from './Results';
import Advisor from './Advisor';
import './styles.scss';

const Quiz = () => {
    const { questions, buttons } = useConfig();
    enum State {
        START,
        QUESTION,
        RESULT
    }

    const [state, setState] = useState(State.START);
    const [score, updateScore] = useState(0);
    const [questionNumber, setQuestionNumber] = useState(1);
    const [currentQuestion, setNextQuestion] = useState(0);
    const [selectedValue, setSelected] = useState(0);
    const numberOfQuestions = questions.length;
    const [answers, setAnswers] = useState(Array(Math.max(numberOfQuestions, 1)).fill(0));
    const compareByNr = (a: Question | Alternative, b: Question | Alternative) => {
        return a.nr - b.nr;
    };

    const sortedQuestions = questions.sort(compareByNr);
    const question = numberOfQuestions > currentQuestion ? sortedQuestions[currentQuestion].question : '';

    const alternatives =
        numberOfQuestions > currentQuestion
            ? sortedQuestions[currentQuestion].alternatives.sort(compareByNr).map(value => value.text)
            : [''];
    const descriptions =
        numberOfQuestions > currentQuestion
            ? sortedQuestions[currentQuestion].alternatives.sort(compareByNr).map(value => value.description)
            : [''];

    const scores =
        numberOfQuestions > currentQuestion
            ? sortedQuestions.map(q => q.alternatives.map(value => value.score))
            : [[0]];

    const values: { text: string; value: number }[] = alternatives.map((alternative, index) => ({
        text: alternative,
        value: index + 1
    }));

    const onChange = (event: { value: string }) => {
        const selected = parseInt(event.value, 10);
        setSelected(selected);
    };

    const getNextQuestion = () => {
        setNextQuestion(currentQuestion + 1);
        setQuestionNumber(questionNumber + 1);
    };
    const getScore = (questionNumber: number, answer: number) => {
        return scores[questionNumber][answer - 1];
    };
    const isRightAnswer = () => {
        return scores[currentQuestion][selectedValue - 1] > 0;
    };
    const getFinalScore = (answers: number[]) => {
        return answers.reduce((accumulator: number, answer: number, i: number) => accumulator + getScore(i, answer), 0);
    };
    const setFinalScore = () => {
        setAnswers(answers => {
            updateScore(score => score + getFinalScore(answers));
            return answers;
        });
    };
    const onClickNext = () => {
        const newAnswers = answers.slice();
        newAnswers[questionNumber - 1] = selectedValue;
        setAnswers(newAnswers);
        if (questionNumber < numberOfQuestions) getNextQuestion();
        setQuestionNumber(questionNumber => {
            setSelected(answers[questionNumber - 1]);
            return questionNumber;
        });
        if (questionNumber === numberOfQuestions) {
            setFinalScore();
            setState(State.RESULT);
        }
    };

    const getPreviousQuestion = () => {
        setQuestionNumber(questionNumber - 1);
        setNextQuestion(currentQuestion - 1);
    };

    const onClickBack = () => {
        getPreviousQuestion();
        setQuestionNumber(questionNumber => {
            const answer = answers[questionNumber - 1];
            setSelected(answer);
            return questionNumber;
        });
    };

    const refreshState = () => {
        setState(State.START);
        updateScore(0);
        setQuestionNumber(1);
        setNextQuestion(0);
        setSelected(0);
        setAnswers(Array(numberOfQuestions).fill(0));
    };

    return (
        <div className="marked-kalkulator-quiz">
            <article className="card">
                <div className="card-content">
                    {state === State.START && <Start onClick={() => setState(State.QUESTION)} />}
                    {state === State.RESULT && <Results score={score} onClick={refreshState} />}
                    {state === State.QUESTION && (
                        <>
                            <div className="progress-text">
                                <span className="theme-text-lightest">{questionNumber}</span>/{numberOfQuestions}
                            </div>
                            <p className="question">{question}</p>
                            <div className={classnames({ 'is-right-answer': isRightAnswer() })}>
                                <ButtonGroup selectedValue={selectedValue} onChange={onChange} values={values} />
                            </div>
                            <div className="footer-container">
                                {selectedValue !== 0 && (
                                    <Advisor
                                        description={descriptions[selectedValue - 1]}
                                        isRightAnswer={isRightAnswer()}
                                        key={selectedValue}
                                    />
                                )}
                                <div className="navigation-buttons">
                                    {questionNumber > 1 && (
                                        <button type="button" className="btn btn-flat" onClick={onClickBack}>
                                            {buttons.back}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-action"
                                        onClick={onClickNext}
                                        disabled={selectedValue === 0}
                                    >
                                        {questionNumber < numberOfQuestions && buttons.next}
                                        {questionNumber === numberOfQuestions && buttons.showResult}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </article>
        </div>
    );
};

export default Quiz;
